var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __copyProps = (to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") {
    for (let key of __getOwnPropNames(from))
      if (!__hasOwnProp.call(to, key) && key !== except)
        __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
  }
  return to;
};
var __reExport = (target, mod, secondTarget) => (__copyProps(target, mod, "default"), secondTarget && __copyProps(secondTarget, mod, "default"));

// src/contracts/schema/http.ts
import { z } from "zod/v4";
var coerceToContractAbsoluteBase = (base) => {
  const validatedUrl = new URL(base);
  const href = validatedUrl.href;
  if (href.endsWith("/")) {
    return href;
  } else {
    return `${href}/`;
  }
};
var normalizeSchemaValue = (value) => {
  if (value !== void 0) {
    return value;
  }
  return z.never();
};
var createContractRequest = (parts) => {
  return {
    pathParams: normalizeSchemaValue(parts?.pathParams),
    queryParams: normalizeSchemaValue(parts?.queryParams),
    body: normalizeSchemaValue(parts?.body)
  };
};
var createContractResponse = (parts) => {
  return { body: normalizeSchemaValue(parts?.body) };
};
var createContract = (args) => {
  const request = createContractRequest({
    pathParams: args.request?.pathParams,
    queryParams: args.request?.queryParams,
    body: args.request?.body
  });
  const response = createContractResponse({
    body: args.response?.body
  });
  return {
    method: args.method,
    path: args.path,
    request,
    response
  };
};
var getContractUrl = (params) => {
  const { path } = params.contract;
  const onlyPath = path.split("?")[0];
  const onlyQueryTemplate = path.split("?")[1];
  const getReplacedPath = () => {
    if (params.pathParams && typeof params.pathParams === "object") {
      return Object.entries(params.pathParams).reduce((acc, [key, value]) => {
        const regex = new RegExp(`:${key}(?=/|$)`, "g");
        return acc.replace(regex, encodeURIComponent(String(value)));
      }, onlyPath);
    }
    return onlyPath;
  };
  const getQueryParts = () => {
    const hasTemplate = typeof onlyQueryTemplate === "string";
    if (params.queryParams && typeof params.queryParams === "object") {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params.queryParams)) {
        if (value === void 0) continue;
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else {
          searchParams.set(key, String(value));
        }
      }
      const queryString2 = searchParams.toString();
      return { queryString: queryString2, includeQuestion: queryString2.length > 0 || hasTemplate };
    }
    if (hasTemplate) {
      return { queryString: `${onlyQueryTemplate}`, includeQuestion: true };
    }
    return { queryString: "", includeQuestion: false };
  };
  const { queryString, includeQuestion } = getQueryParts();
  if (typeof URL === "function") {
    const url = new URL(`.${getReplacedPath()}${includeQuestion ? `?${queryString}` : ""}`, params.base);
    return url.toString();
  } else {
    const baseWithoutTrailingSlash = params.base.endsWith("/") ? params.base.slice(0, -1) : params.base;
    return `${baseWithoutTrailingSlash}${getReplacedPath()}${includeQuestion ? `?${queryString}` : ""}`;
  }
};
var withContractBase = (baseParams) => {
  return {
    withFetchParams: (urlParams) => {
      const url = getContractUrl({
        contract: baseParams.contract,
        base: baseParams.base,
        pathParams: urlParams.pathParams,
        queryParams: urlParams.queryParams
      });
      const { method, response } = baseParams.contract;
      const defaultFetcher = async (args) => {
        const response2 = await fetch(args.url, {
          method: args.method,
          body: args.body ? JSON.stringify(args.body) : void 0
        });
        return response2.json();
      };
      const dataHandlers = (data) => {
        return {
          toRaw: () => data,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          toParsed: async () => response.body.parse(await data)
        };
      };
      return {
        withFetcher: ((fetcher) => {
          const effectiveFetcher = fetcher ?? defaultFetcher;
          return dataHandlers(effectiveFetcher({ method, url, body: urlParams.body }));
        })
      };
    }
  };
};

// src/contracts/data-dump-http.ts
import * as z2 from "zod/v4";
var DataDumpSourceSchema = z2.union([
  z2.literal("machine-learning-face-detection"),
  z2.literal("machine-learning-object-detection"),
  z2.literal("machine-learning-audio-transcription"),
  z2.templateLiteral(["machine-learning-face-detection-v", z2.number().min(1).positive()]),
  z2.templateLiteral(["machine-learning-object-detection-v", z2.number().min(1).positive()])
]);
var DataDumpMimeTypeSchema = z2.union([
  z2.literal("application/json"),
  z2.literal("application/octet-stream"),
  z2.literal("application/xml")
]);
var DataDumpGrantContract = createContract({
  method: "POST",
  path: "/data-dump/:proctoringSessionUuid/grant",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    }),
    body: z2.object({
      source: DataDumpSourceSchema,
      mimeType: DataDumpMimeTypeSchema
    })
  },
  response: {
    body: z2.object({
      grantId: z2.string(),
      uploadUrl: z2.string()
    })
  }
});
var DataDumpCallbackContract = createContract({
  method: "POST",
  path: "/data-dump/:proctoringSessionUuid/callback",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    }),
    body: z2.object({
      grantId: z2.string()
    })
  }
});
var DataDumpLinksContract = createContract({
  method: "GET",
  path: "/data-dump/:proctoringSessionUuid/links",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    })
  },
  response: {
    body: z2.object({
      links: z2.array(
        z2.object({
          source: DataDumpSourceSchema,
          mimeType: DataDumpMimeTypeSchema,
          downloadUrl: z2.string()
        })
      )
    })
  }
});

// src/contracts/logging-http.ts
import * as z3 from "zod/v4";
var MachineLearningEventSchema = z3.object({
  type: z3.literal("machine-learning"),
  time: z3.date()
});
var FaceDetectionStateSchema = z3.union([
  z3.literal("detected-no-face"),
  z3.literal("detected-target-face"),
  z3.literal("detected-unknown-face"),
  z3.literal("detected-many-faces")
]);
var FaceDetectionEventSchema = MachineLearningEventSchema.extend({
  name: z3.literal("face-detection"),
  details: z3.object({
    previousState: FaceDetectionStateSchema,
    currentState: FaceDetectionStateSchema
  })
});
var TargetObjectsSchema = z3.union([z3.literal("cell-phone"), z3.literal("laptop"), z3.literal("book")]);
var ObjectDetectionStateSchema = z3.union([
  z3.literal("detected-no-object"),
  z3.templateLiteral(["detected-", TargetObjectsSchema], "-object"),
  z3.literal("detected-many-objects")
]);
var ObjectDetectionEventSchema = MachineLearningEventSchema.extend({
  name: z3.literal("object-detection"),
  details: z3.object({
    previousState: ObjectDetectionStateSchema,
    currentState: ObjectDetectionStateSchema
  })
});
var FlaggedKeywordEventSchema = MachineLearningEventSchema.extend({
  name: z3.literal("flagged-keyword"),
  details: z3.object({
    keyword: z3.string()
  })
});
var LogMachineLearningQuizEventContract = createContract({
  method: "POST",
  path: "/quiz/event/:proctoringSessionUuid",
  request: {
    pathParams: z3.object({
      proctoringSessionUuid: z3.string()
    }),
    body: z3.union([FaceDetectionEventSchema, ObjectDetectionEventSchema, FlaggedKeywordEventSchema])
  }
});

// src/contracts/restricted-device-event.ts
import * as z6 from "zod/v4";

// src/contracts/json/index.ts
import { z as z4 } from "zod";
var FlaggedKeywordsSchema = z4.union([
  z4.array(z4.object({ keyword: z4.string(), level: z4.number().optional() })),
  z4.null()
]);
var RestrictedDeviceOptionsSchema = z4.object({
  restrictDevice: z4.boolean(),
  videoRecordingMode: z4.union([z4.literal("off"), z4.literal("normal"), z4.literal("strict")])
});
var RestrictedDeviceStateDataSchema = z4.object({
  isLocked: z4.boolean(),
  recordingStatus: z4.optional(
    z4.object({
      video: z4.optional(
        z4.object({
          selectedCamera: z4.optional(z4.union([z4.literal("front"), z4.literal("back")])),
          isRecording: z4.boolean()
        })
      )
    })
  )
});

// src/contracts/schema/event.ts
import * as z5 from "zod/v4";
var createEventContract = (namespace, handshake, events) => {
  return {
    namespace,
    queryParams: handshake.queryParams,
    events
  };
};
var createDiscriminableEventPayload = (key, schema) => {
  return z5.object({
    key: z5.literal(key),
    ...schema.shape
  });
};
var createDiscriminableEvent = (...args) => {
  return z5.discriminatedUnion("key", args);
};

// src/contracts/restricted-device-event.ts
var RestrictedDeviceEventContract = createEventContract(
  "/restricted-device",
  {
    queryParams: z6.object({
      secret: z6.string(),
      peerId: z6.string()
    })
  },
  {
    message: createDiscriminableEvent(
      createDiscriminableEventPayload("pollRequest", z6.object({})),
      createDiscriminableEventPayload(
        "pollResponse",
        z6.object({
          devices: z6.array(
            z6.object({
              peerId: z6.string(),
              connected: z6.boolean(),
              data: z6.any()
            })
          )
        })
      ),
      createDiscriminableEventPayload(
        "options",
        RestrictedDeviceOptionsSchema.extend({
          proctoringSessionId: z6.optional(z6.string()),
          accountId: z6.optional(z6.string()),
          authCode: z6.optional(z6.string())
        })
      ),
      createDiscriminableEventPayload("stateUpdate", RestrictedDeviceStateDataSchema)
    )
  }
);

// src/contracts/restricted-device-http.ts
import * as z8 from "zod/v4";

// src/contracts/restricted-device-common.ts
import * as z7 from "zod";

// src/contracts/schema/db.ts
var db_exports = {};
__reExport(db_exports, zod_star);
import * as zod_star from "@verity/database/zod";

// src/contracts/restricted-device-common.ts
var RestrictedDeviceLoggedEventRootSchema = z7.object({
  ...db_exports.ProctoringSessionEventSchema.shape,
  type: z7.literal("restricted-device")
});
var SharedEventDetailsSchema = z7.object({
  peer: z7.object({
    ...db_exports.RestrictedDevicePeerSchema.omit({
      proctoringSessionId: true,
      options: true
    }).shape,
    options: z7.union([RestrictedDeviceOptionsSchema, z7.null()])
  })
});
var RecordingEventDetailsSchema = z7.object({
  ...SharedEventDetailsSchema.shape,
  source: z7.object({ type: z7.literal("video") })
});
var RestrictedDeviceLoggedEventSchema = z7.discriminatedUnion("name", [
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("state"),
    details: z7.object({
      ...SharedEventDetailsSchema.shape,
      state: z7.object({
        ...db_exports.RestrictedDeviceStateSchema.pick({
          connected: true
        }).shape,
        data: z7.union([RestrictedDeviceStateDataSchema, z7.null()])
      })
    })
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("connect"),
    details: SharedEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("disconnect"),
    details: SharedEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("lock"),
    details: SharedEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("unlock"),
    details: SharedEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("start recording"),
    details: RecordingEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("end recording"),
    details: RecordingEventDetailsSchema
  })
]);

// src/contracts/restricted-device-http.ts
var RestrictedDeviceRegistrationContract = createContract({
  method: "POST",
  path: "/restricted-device/register",
  request: {
    body: z8.object({
      role: db_exports.RestrictedDeviceRoleSchema,
      platform: db_exports.RestrictedDevicePlatformSchema,
      proctoringSessionUuid: z8.string(),
      options: z8.optional(RestrictedDeviceOptionsSchema)
    })
  },
  response: {
    body: z8.object({
      peerId: z8.string()
    })
  }
});
var RestrictedDeviceDeletePeerContract = createContract({
  method: "DELETE",
  path: "/restricted-device/:restrictedDevicePeerId",
  request: {
    pathParams: z8.object({
      restrictedDevicePeerId: z8.string()
    })
  }
});
var RestrictedDeviceGrantContract = createContract({
  method: "POST",
  path: "/restricted-device/:restrictedDevicePeerId/grant",
  request: {
    pathParams: z8.object({
      restrictedDevicePeerId: z8.string()
    })
  },
  response: {
    body: z8.object({
      secret: z8.string()
    })
  }
});

// src/contracts/inference-http.ts
import * as z9 from "zod/v4";
var CreateInferenceTokenContract = createContract({
  method: "POST",
  path: "/inference/token",
  request: {
    body: z9.object({
      proctoringSessionUuid: z9.string()
    })
  },
  response: {
    body: z9.object({
      token: z9.string()
    })
  }
});

// src/contracts/proctoring-session-http.ts
import * as z10 from "zod/v4";
var GetProctoringSessionFlaggedKeywordsContract = createContract({
  method: "GET",
  path: "/proctoring-session/:proctoringSessionId/flagged-keywords",
  request: {
    pathParams: z10.object({
      proctoringSessionId: z10.string()
    })
  },
  response: {
    body: z10.array(z10.string())
  }
});
var export_RestrictedDevicePlatformSchema = db_exports.RestrictedDevicePlatformSchema;
var export_RestrictedDeviceRoleSchema = db_exports.RestrictedDeviceRoleSchema;
export {
  CreateInferenceTokenContract,
  DataDumpCallbackContract,
  DataDumpGrantContract,
  DataDumpLinksContract,
  DataDumpMimeTypeSchema,
  DataDumpSourceSchema,
  FaceDetectionEventSchema,
  FaceDetectionStateSchema,
  FlaggedKeywordEventSchema,
  FlaggedKeywordsSchema,
  GetProctoringSessionFlaggedKeywordsContract,
  LogMachineLearningQuizEventContract,
  MachineLearningEventSchema,
  ObjectDetectionEventSchema,
  ObjectDetectionStateSchema,
  RestrictedDeviceDeletePeerContract,
  RestrictedDeviceEventContract,
  RestrictedDeviceGrantContract,
  RestrictedDeviceLoggedEventRootSchema,
  RestrictedDeviceLoggedEventSchema,
  RestrictedDeviceOptionsSchema,
  export_RestrictedDevicePlatformSchema as RestrictedDevicePlatformSchema,
  RestrictedDeviceRegistrationContract,
  export_RestrictedDeviceRoleSchema as RestrictedDeviceRoleSchema,
  RestrictedDeviceStateDataSchema,
  TargetObjectsSchema,
  coerceToContractAbsoluteBase,
  getContractUrl,
  withContractBase
};
//# sourceMappingURL=index.js.map